import classNames from "classnames";
import * as React from "react";
import { Link } from "react-router";

import CopyToClipboardButton from "@/shared/components/CopyToClipboardButton";
import { EventName, EventProps } from "@/shared/services/analytics";

import styles from "./DetailsRail.module.css";

/**
 * The shaded column beside the main content of a details page (a file's, a
 * device's, a device type's) that lists facts such as IDs, sizes and dates.
 * Holds {@link DetailsRailSection}s.
 */
export function DetailsRail({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <aside className={classNames(styles.rail, className)}>{children}</aside>
  );
}

interface DetailsRailSectionProps {
  label: string;
}

/** One fact in a {@link DetailsRail}, under a small heading that names it. */
export function DetailsRailSection({
  label,
  children,
}: React.PropsWithChildren<DetailsRailSectionProps>) {
  const headingId = React.useId();

  return (
    <section aria-labelledby={headingId} className={styles.section}>
      <h3 id={headingId} className={styles.sectionHeader}>
        {label}
      </h3>
      <div className={styles.sectionContent}>{children}</div>
    </section>
  );
}

/**
 * A link in a {@link DetailsRail}, colored like the linked IDs of
 * {@link CopyableValue}.
 */
export function DetailsRailLink(props: React.ComponentProps<typeof Link>) {
  return (
    <Link {...props} className={classNames(styles.link, props.className)} />
  );
}

interface CopyableValueProps {
  text: string;
  /** Route the value links to. Without it the value is plain text. */
  to?: string;
  eventName?: EventName;
  eventProps?: EventProps<EventName>;
}

/** An ID or path in monospace, with a button that copies it. */
export function CopyableValue({
  text,
  to,
  eventName,
  eventProps,
}: CopyableValueProps) {
  return (
    <div className={classNames(styles.sectionContent, styles.copyableContent)}>
      <span className={styles.copyableText}>
        {to ? (
          <Link className={styles.link} to={to}>
            {text}
          </Link>
        ) : (
          text
        )}
      </span>
      <CopyToClipboardButton
        text={text}
        eventName={eventName}
        eventProps={eventProps}
      />
    </div>
  );
}
